import React, { useMemo } from "react";
// @mui
import { CssBaseline, ThemeOptions } from "@mui/material";
import {
    createTheme,
    ThemeProvider as MUIThemeProvider,
    StyledEngineProvider,
} from "@mui/material/styles";
// hooks
import useSettings from "../hooks/useSettings";
//
import palette from "./palette";
import typography from "./typography";
import breakpoints from "./breakpoints";
import componentsOverride from "./overrides";
import shadows from "./shadows";
import { darkThemePreset } from "@callveriq/theme/palette";
import { isDefined } from "@callveriq/utils/helper/common.helper";

// ----------------------------------------------------------------------

export default function ThemeProvider({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const { themeMode, themeColorPresets } = useSettings();
    const isLight = themeMode === "light";

    const themeOptions: ThemeOptions = useMemo(
        (): ThemeOptions => ({
            palette: isLight
                ? palette.light
                : {
                      ...palette.dark,
                      ...(isDefined(themeColorPresets) &&
                          themeColorPresets in darkThemePreset && {
                              ...darkThemePreset[
                                  themeColorPresets as keyof typeof darkThemePreset
                              ],
                          }),
                  },
            typography,
            breakpoints,
            spacing: 8,
            shape: { borderRadius: 4 },
            shadows: isLight ? shadows.light : shadows.dark,
        }),
        [isLight, themeColorPresets]
    );

    const theme = createTheme(themeOptions);
    theme.components = componentsOverride(theme);

    return (
        <StyledEngineProvider injectFirst>
            <MUIThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MUIThemeProvider>
        </StyledEngineProvider>
    );
}
